/**
 * Auto-adds a free Foraged Tote Bag when a Beef Boxes product is in the cart,
 * and removes auto-added totes when boxes are removed.
 * Works with the Shopify automatic BXGY discount "Free Tote Bag with Beef Box".
 */
(function () {
  const PROPERTY_KEY = '_free_tote_gift';
  const SOURCE = 'free-tote-gift';

  let syncing = false;
  let queued = false;

  function getConfig() {
    return window.freeToteGift || null;
  }

  function isPromotionActive(config) {
    if (!config?.enabled || !config.toteVariantId) return false;
    const now = Date.now();
    if (config.startsAt) {
      const start = Date.parse(config.startsAt);
      if (!Number.isNaN(start) && now < start) return false;
    }
    if (config.endsAt) {
      const end = Date.parse(config.endsAt);
      if (!Number.isNaN(end) && now > end) return false;
    }
    return true;
  }

  function isBeefBoxItem(item, config) {
    const productIds = config.beefBoxProductIds || [];
    return productIds.includes(Number(item.product_id)) || productIds.includes(String(item.product_id));
  }

  function isFreeGiftItem(item) {
    const props = item.properties || {};
    return props[PROPERTY_KEY] === 'true' || props[PROPERTY_KEY] === true;
  }

  async function fetchCart() {
    const response = await fetch(`${routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  }

  async function addFreeTotes(variantId, quantity) {
    const response = await fetch(`${routes.cart_add_url}.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity,
            properties: { [PROPERTY_KEY]: 'true' },
          },
        ],
      }),
    });
    const data = await response.json();
    if (data.status) {
      console.warn('[free-tote-gift] Could not add tote:', data.description || data.message);
      return false;
    }
    return true;
  }

  async function changeLineQuantity(lineKey, quantity) {
    const response = await fetch(`${routes.cart_change_url}.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: lineKey, quantity }),
    });
    const data = await response.json();
    if (data.status) {
      console.warn('[free-tote-gift] Could not update tote line:', data.description || data.message);
      return false;
    }
    return true;
  }

  async function reduceFreeGiftQuantity(giftItems, amountToRemove) {
    let remaining = amountToRemove;
    // Remove from the end so earlier gift lines stay stable in the cart UI.
    for (let i = giftItems.length - 1; i >= 0 && remaining > 0; i -= 1) {
      const item = giftItems[i];
      const removeQty = Math.min(item.quantity, remaining);
      const nextQty = item.quantity - removeQty;
      const ok = await changeLineQuantity(item.key, nextQty);
      if (!ok) break;
      remaining -= removeQty;
    }
    return remaining === 0;
  }

  async function syncFreeToteGift() {
    const config = getConfig();
    if (!isPromotionActive(config)) return false;

    const cart = await fetchCart();
    const items = cart.items || [];

    const boxQty = items.reduce((sum, item) => (isBeefBoxItem(item, config) ? sum + item.quantity : sum), 0);
    const giftItems = items.filter(isFreeGiftItem);
    const giftQty = giftItems.reduce((sum, item) => sum + item.quantity, 0);

    if (boxQty === giftQty) return false;

    if (boxQty > giftQty) {
      return addFreeTotes(config.toteVariantId, boxQty - giftQty);
    }

    return reduceFreeGiftQuantity(giftItems, giftQty - boxQty);
  }

  async function runSync(event) {
    if (event?.source === SOURCE) return;
    if (syncing) {
      queued = true;
      return;
    }

    syncing = true;
    let changed = false;
    try {
      changed = await syncFreeToteGift();
    } catch (error) {
      console.error('[free-tote-gift]', error);
    } finally {
      syncing = false;
    }

    if (queued) {
      queued = false;
      return runSync({ source: 'queued' });
    }

    if (changed && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      return publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE });
    }
  }

  function init() {
    if (!getConfig()?.enabled) return;

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => runSync(event));
    }

    // Catch carts that already contain a box (e.g. returning visitors).
    runSync({ source: 'init' });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
